import * as React from 'react'

/* TYPES */

interface I18n {
  l: () => any
  t: () => any
  translations: {}
}

interface I18nConfig {
  I18n: I18n
}

interface I18nApi {
  l: () => any
  t: () => any
}

interface Props extends I18nConfig {
  children?: React.ReactNode
}

interface RenderProps extends I18nConfig {
  children: (props: I18nApi) => {}
}

/* HOOK */

export const useI18n = ({ I18n }: I18nConfig) => {
  const [value, _] = React.useState(I18n)

  return value
}

/* DECORATOR */

export const withI18n = <T extends {}>(config: I18nConfig) => (
  Component: React.ComponentType<T>
) => (props: T & { I18n: I18nConfig }) => (
  <Component {...props} I18n={useI18n({ ...config, ...props.I18n })} />
)
/* RENDER PROP */

export const I18n = ({ children, ...props }: RenderProps) => {
  const I18n = useI18n(props)

  return typeof children === 'function' ? children(I18n) : children
}

/* CONTEXT */

const noop = (method: string) => () =>
  console.warn(
    `Could not call \`${method}\`, please wrap your component in a \`<I18nProvider>\``
  )

export const I18nContext = React.createContext<I18nApi>({
  l: noop('l'),
  t: noop('t'),
})

export const I18nProvider = ({ children, ...props }: Props) => (
  <I18nContext.Provider value={useI18n(props)}>{children}</I18nContext.Provider>
)

export const consumeI18n = () => React.useContext(I18nContext)

export default {
  Provider: ({ children, I18n }: any) => (
    <I18nContext.Provider value={I18n}>{children}</I18nContext.Provider>
  ),
  Consumer: ({ children }: any) => (
    <I18nContext.Consumer>{I18n => children({ I18n })}</I18nContext.Consumer>
  ),
  withProvider: (Component: any) => ({ I18n, ...props }: any) => (
    <I18nContext.Provider value={I18n}>
      <Component {...props} />
    </I18nContext.Provider>
  ),
  withConsumer: (Component: any) => (props: any) => (
    <I18nContext.Consumer>
      {I18n => <Component I18n={I18n} {...props} />}
    </I18nContext.Consumer>
  ),
}
